import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import { firstValueFrom } from 'rxjs';

/**
 * The FE's only route to a policy approval transition (OE-75, design
 * `docs/categorized/features/POLICY-APPROVAL-CHAIN-DESIGN.md` §2e).
 *
 * The dialogs used to move a policy to `pending_approval` / `approved` with a
 * direct Firestore write, which meant the approval chain was decorative: the
 * client decided the outcome and nothing checked who was asking. Every
 * transition now goes to `api-policyApproval` (OE-74), where the pure engine
 * owns the state machine and the endpoint owns authorization — including the
 * one rule the browser cannot enforce, that only the CURRENT approver may
 * decide the current step.
 *
 * `apiBaseUrl` is passed per call rather than held on the service: the two
 * callers already receive it by different routes (the policy dialog through
 * `MAT_DIALOG_DATA`, the details dialog through `OrgDialogService`), and
 * keeping it out of service state leaves this class trivially testable.
 * The `/api/` fallback mirrors `OrgCreationService`.
 */

export type PolicyApprovalAction = 'submit' | 'decide' | 'override' | 'resubmit';

export interface PolicyApprovalCallOptions {
    orgKey: string;
    policyKey: string;
    /** Cloud Functions base (e.g. `https://…/api-`); falls back to `/api/`. */
    apiBaseUrl?: string;
}

export interface PolicyDecideOptions extends PolicyApprovalCallOptions {
    decision: 'approved' | 'rejected';
    /** Required by the server on a rejection. */
    comments?: string;
}

export interface PolicyOverrideOptions extends PolicyApprovalCallOptions {
    reason: string;
}

/** Mirrors the endpoint's `data` block (`PolicyApprovalResponseData`). */
export interface PolicyApprovalResponseData {
    policyKey: string;
    policyName: string;
    status: string;
    workflowStatus?: string;
    currentStepIndex?: number;
    awaitingApproverUid?: string;
    notificationsSent?: number;
}

export interface PolicyApprovalOutcome {
    ok: boolean;
    message: string;
    /** Machine-readable code — engine codes (`ERR_*`) pass through verbatim. */
    error?: string;
    details?: Record<string, unknown>;
    data?: PolicyApprovalResponseData;
}

interface PolicyApprovalHttpResponse {
    success: boolean;
    message: string;
    error?: string;
    details?: Record<string, unknown>;
    data?: PolicyApprovalResponseData;
}

@Injectable({ providedIn: 'root' })
export class PolicyApprovalService {
    private http = inject(HttpClient);
    private auth = inject(AngularFireAuth);

    /** Start the chain (or, with no workflow configured, approve outright). */
    public submit(options: PolicyApprovalCallOptions): Promise<PolicyApprovalOutcome> {
        return this.perform('submit', options);
    }

    /** Re-enter the chain at step 0 after a rejection. */
    public resubmit(options: PolicyApprovalCallOptions): Promise<PolicyApprovalOutcome> {
        return this.perform('resubmit', options);
    }

    /** Approve or reject the current step. The server checks the approver. */
    public decide(options: PolicyDecideOptions): Promise<PolicyApprovalOutcome> {
        return this.perform('decide', options, {
            decision: options.decision,
            ...(options.comments ? { comments: options.comments } : {}),
        });
    }

    /** Admin/owner bypass of the remaining steps. Reason is mandatory. */
    public override(options: PolicyOverrideOptions): Promise<PolicyApprovalOutcome> {
        return this.perform('override', options, { reason: options.reason });
    }

    private async perform(
        action: PolicyApprovalAction,
        options: PolicyApprovalCallOptions,
        extra: Record<string, unknown> = {}
    ): Promise<PolicyApprovalOutcome> {
        const idToken = await this.getIdToken();
        if (!idToken) {
            return {
                ok: false,
                error: 'UNAUTHENTICATED',
                message: 'You must be signed in to action a policy approval.',
            };
        }

        const url = options.apiBaseUrl ? `${options.apiBaseUrl}policyApproval-default` : `/api/policyApproval-default`;

        try {
            const response = await firstValueFrom(
                this.http.post<PolicyApprovalHttpResponse>(url, {
                    action,
                    orgKey: options.orgKey,
                    policyKey: options.policyKey,
                    idToken,
                    ...extra,
                })
            );

            return {
                ok: response?.success === true,
                message: response?.message || (response?.success ? 'Done' : 'The approval action was refused.'),
                ...(response?.error ? { error: response.error } : {}),
                ...(response?.details ? { details: response.details } : {}),
                ...(response?.data ? { data: response.data } : {}),
            };
        } catch (err: unknown) {
            // A refusal arrives as an HTTP error (403/409/422) carrying the
            // endpoint's own body — surface that, never a generic failure.
            const body = (err as { error?: PolicyApprovalHttpResponse })?.error;
            return {
                ok: false,
                message: body?.message || 'Could not reach the approval service. Please try again.',
                error: body?.error || 'REQUEST_FAILED',
                ...(body?.details ? { details: body.details } : {}),
            };
        }
    }

    private async getIdToken(): Promise<string> {
        const user = await this.auth.currentUser;
        return user ? user.getIdToken() : '';
    }
}
